import { useGetLocale } from '@legacy/components/global/LocaleProvider';
import { Trans, useTranslation } from 'react-i18next';
import { translationAnyText } from '@legacy/utils/translation';
import {
  useGetAddedValuePerMonthPrivilegeOptions,
  useGetAddedValuePerMonthReportingWidget,
} from '@legacy/apis/reportingWidgetsAPI';
import { IAddedValuePerMonthWidgetDataItem } from '@legacy/apis/reportingWidgetsAPI/types/addedValuePerMonth';
import { useState, MouseEvent } from 'react';
import {
  MenuItem,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Box,
  CircularProgress,
  Select,
} from '@legacy/components/mui-components';

import { currencyFormatter } from '@legacy/utils/currency';
import { ReportingWidgetCard } from '../ReportingWidgetCard';
import { WidgetEmpty, WidgetError } from '../ReportingWidgetState';
import { convertPrivilegeOptionsToFilterOptions } from './utils';
import { CustomTooltip } from './components';
import { BarChart } from '../BarChart';

export const AddedValuePerMonthReportingWidget = () => {
  const locale = useGetLocale();
  const { t } = useTranslation('reportingWidgets');
  const { privilegeOptions } = useGetAddedValuePerMonthPrivilegeOptions();

  const [alignment, setAlignment] = useState('reg');
  const [valueFilter, setValueFilter] = useState('1');

  const { widgetName, widgetData, currencyAbb, isError, isLoading, isEmpty } =
    useGetAddedValuePerMonthReportingWidget(valueFilter);

  const handleSelectChange = (event: SelectChangeEvent) => {
    setValueFilter(event.target.value as string);
  };

  const handleAlignmentChange = (
    event: MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  const parseRowData = (data: IAddedValuePerMonthWidgetDataItem[]) =>
    data.map((item) => ({
      ...item,
      month: translationAnyText(t, `Month${item.month}`),
    }));

  const parsedWidgetData = parseRowData(widgetData);

  const valueFormatter = (value: number | null) =>
    currencyFormatter(locale, 2).format(value ?? 0);

  const renderWidgetContentBasedOnState = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (isError) {
      return <WidgetError />;
    }
    if (isEmpty) {
      return <WidgetEmpty noDataMessage={t('EmptyStateGeneralAlertMessage')} />;
    }

    return (
      <BarChart
        widgetName={widgetName}
        parsedWidgetData={parsedWidgetData}
        alignment={alignment}
        xAxis={[
          {
            scaleType: 'band',
            dataKey: 'month',
            tickPlacement: 'middle',
            barGapRatio: 0,
            categoryGapRatio: 0.5,
            // To fix param barGapRatio not resolve in typescript issue
            // https://github.com/mui/mui-x/issues/10334
          } as any,
        ]}
        yAxis={[
          {
            label: currencyAbb?.toString() ?? '',
            labelStyle: {
              fontSize: 12,
            },
            valueFormatter,
            width: 120,
          },
        ]}
        series={[
          {
            type: 'bar',
            dataKey:
              alignment === 'reg' ? 'amountRegistered' : 'amountEstimated',
            label:
              alignment === 'reg'
                ? `${t('AddedValuePerMonthRegAmount')} ${currencyAbb?.toString() ?? ''}`
                : `${t('AddedValuePerMonthEstAmount')} ${currencyAbb?.toString() ?? ''}`,
            valueFormatter,
          },
        ]}
        Tooltip={
          <CustomTooltip
            data={parsedWidgetData}
            alignment={alignment}
            currencyAbb={currencyAbb}
            valueFormatter={valueFormatter}
          />
        }
      />
    );
  };

  const filterOptions = convertPrivilegeOptionsToFilterOptions(
    privilegeOptions,
    t,
  );

  return (
    <ReportingWidgetCard
      name={widgetName}
      title={t('AddedValuePerMonth')}
      infoIconText={
        <Trans
          i18nKey="AddedValuePerMonthWidgetDescription"
          key="AddedValuePerMonthWidgetDescription"
          defaults={t('AddedValuePerMonthWidgetDescription')}
          components={{ 1: <br /> }}
          values={{ currency: currencyAbb?.toString() }}
        />
      }
      toggleContent={
        <Stack direction="row" sx={{ mr: 3 }}>
          <Select
            labelId="filter-label"
            id="filter-select"
            value={valueFilter}
            label={t('Filter')}
            onChange={handleSelectChange}
            size="small"
            fullWidth
            data-automation-id="AddedValuePerMonthFilterSelect"
            sx={{ minWidth: 180 }}
          >
            {filterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <ToggleButtonGroup
            size="small"
            value={alignment}
            exclusive
            onChange={handleAlignmentChange}
            aria-label={t('AddedValuePerMonth')}
            sx={{ ml: 1 }}
          >
            <ToggleButton
              value="reg"
              title={t('AddedValuePerMonthShowRegisteredValue')}
              data-automation-id="AddedValuePerMonthRegButton"
            >
              {t('RegText')}
            </ToggleButton>
            <ToggleButton
              value="est"
              title={t('AddedValuePerMonthShowEstimatedValue')}
              data-automation-id="AddedValuePerMonthEstButton"
            >
              {t('EstText')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      }
      hasAction
    >
      {renderWidgetContentBasedOnState()}
    </ReportingWidgetCard>
  );
};
